/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled'
import React, { useState } from 'react'
import { message } from 'antd'
import {
  CloseOutlined,
  InfoCircleOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Button from 'src/components/ts-components/Button'
import WarningModal from 'src/components/WarningModal'

const BannerContainer = styled.div`
  background: ${colors.PRIMARY_50};
  border: 1px solid ${colors.PRIMARY_100};
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`

const TopContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
`

const IconContainer = styled.div`
  color: ${colors.PRIMARY_400};
  font-size: 20px;
  display: flex;
  align-items: center;
`

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

// Pulls the 32px close button's box out by its 8px inner padding, so the X
// glyph lines up with the title and the card edge without shrinking the target.
const CloseButtonWrap = styled.div`
  display: flex;
  margin: -8px -8px -8px 0;
`

const Title = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.SECONDARY_400};
`

const Description = styled.div`
  font-size: 12px;
  color: ${colors.SECONDARY_300};
`

const ContinueButton = styled.button`
  background: ${colors.PRIMARY};
  color: ${PRIMARY_BUTTON_CONTENT.text};
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: background-color 0.2s ease;
  width: 100%;

  &:hover {
    background: ${colors.PRIMARY_400};
  }

  &:active {
    background: ${colors.PRIMARY_500};
  }
`

interface ContinueTutorialBannerProps {
  onContinue: () => void
  /** Permanently dismisses the wizard; the banner is hidden once it resolves. */
  onDismiss: () => Promise<void>
}

/**
 * Banner component displayed in the sidebar when the onboarding tutorial is skipped
 * Allows users to resume the tutorial setup wizard, or dismiss it for good
 */
const ContinueTutorialBanner: React.FC<ContinueTutorialBannerProps> = ({
  onContinue,
  onDismiss,
}) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [isDismissing, setIsDismissing] = useState(false)

  const handleConfirmDismiss = async () => {
    setIsDismissing(true)
    try {
      await onDismiss()
    } catch {
      setIsDismissing(false)
      message.error('Something went wrong. Please try again.')
    }
  }

  return (
    <BannerContainer>
      <TopContent>
        <IconContainer>
          <InfoCircleOutlined />
        </IconContainer>
        <ContentContainer>
          <Title>Complete Your Setup</Title>
          <Description>
            Continue where you left off with the setup wizard
          </Description>
        </ContentContainer>
        <CloseButtonWrap>
          <Button
            size='compact'
            variant='ghost'
            iconOnly
            text={capitalizeWords('dismiss setup wizard')}
            icon={<CloseOutlined />}
            contentColor={colors.SECONDARY_300}
            onClick={() => setIsConfirmOpen(true)}
          />
        </CloseButtonWrap>
      </TopContent>
      <ContinueButton onClick={onContinue}>
        <PlayCircleOutlined />
        Continue
      </ContinueButton>
      <WarningModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmDismiss}
        title={capitalizeWords('skip setup wizard?')}
        description="If you skip the setup wizard, you won't be able to get back to it. Are you sure?"
        confirmLabel='SKIP'
        isLoading={isDismissing}
      />
    </BannerContainer>
  )
}

export default ContinueTutorialBanner
